// src/components/exam/ExamAnalytics.jsx - UPDATED WITH RANGE SELECTOR
import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import examAnalyticsService from '../../services/examAnalyticsService';
import ProgressMetricsCards from './charts/ProgressMetricsCards';
import OverallPerformanceChart from './charts/OverallPerformanceChart';
import SectionRadarChart from './charts/SectionRadarChart';
import SectionTrendCharts from './charts/SectionTrendCharts';
import StrengthsWeaknesses from './charts/StrengthsWeaknesses';
import { AlertCircle, BarChart3, Download, RefreshCw } from 'lucide-react';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

const ExamAnalytics = ({ currentExam, examData }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [examRange, setExamRange] = useState(10); // Default: last 10 exams
  const [refreshing, setRefreshing] = useState(false);

  // Fetch analytics data
  const fetchAnalytics = async (limit = examRange) => {
    if (!auth.currentUser || !examData) return;

    try {
      setLoading(true);
      setError(null);
      const userId = auth.currentUser.uid;

      console.log(`📊 Fetching analytics for last ${limit} exams...`);

      // 1. Fetch history from service with limit
      const history = await examAnalyticsService.getExamHistory(
        userId,
        examData.examProvider,
        examData.level,
        limit // Pass the selected range
      );

      console.log(`✅ Loaded ${history.length} exam results`);

      // 2. Calculate all metrics using the service
      const overall = examAnalyticsService.calculateOverallMetrics(history);
      const sections = examAnalyticsService.calculateSectionMetrics(history);
      const time = examAnalyticsService.calculateTimeAnalysis(history);
      const comparison = examAnalyticsService.compareWithPrevious(currentExam, history);
      const radarData = examAnalyticsService.prepareRadarChartData(sections, currentExam);
      const { strengths, weaknesses } = examAnalyticsService.identifyStrengthsWeaknesses(sections);

      setData({
        overall,
        sections,
        time,
        comparison,
        radarData,
        strengths,
        weaknesses,
        historyCount: history.length,
        requestedRange: limit
      });
    } catch (err) {
      console.error("Failed to load analytics:", err);
      setError("Could not load analytics data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load, depend only on stable identifiers, not the full objects.
  // currentExam is re-created on every Firestore snapshot (even when data hasn't changed),
  // so using the full object as a dep would cause an infinite fetch loop whenever a
  // write-back (e.g. writingFeedback) triggers a snapshot update.
  const currentExamId = currentExam?.id ?? currentExam?.examId ?? null;
  const examProvider = examData?.examProvider ?? null;
  const examLevel = examData?.level ?? null;

  useEffect(() => {
    fetchAnalytics(examRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExamId, examProvider, examLevel]);

  // Handle range change
  const handleRangeChange = (newRange) => {
    setExamRange(newRange);
    fetchAnalytics(newRange);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics(examRange);
  };

  // Handle export (optional - creates JSON download)
  const handleExport = () => {
    if (!data) return;

    const exportData = {
      exam: {
        provider: examData.examProvider,
        level: examData.level,
        exportDate: new Date().toISOString()
      },
      currentExam: {
        score: currentExam.overallScore,
        date: currentExam.completedAt
      },
      analytics: {
        overall: data.overall,
        sections: data.sections,
        strengths: data.strengths,
        weaknesses: data.weaknesses
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `exam-analytics-${examData.examProvider}-${examData.level}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-gray-200 m-8">
        <LogamiLogoSpinner size="xl" className="mb-4" decorative={false} alt="Logami loading" />
        <p className="text-gray-700 font-semibold text-lg">Analyzing your performance...</p>
        <p className="text-gray-500 text-sm mt-2">Loading exam history data</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-red-50 rounded-xl border border-red-200 m-8">
        <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
        <p className="text-red-700 font-semibold text-lg">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  // Empty state - no history
  if (!data || data.historyCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border-2 border-dashed border-gray-300 m-8">
        <BarChart3 className="w-16 h-16 text-gray-400 mb-4" />
        <p className="text-gray-700 font-semibold text-lg mb-2">No Previous Exams Found</p>
        <p className="text-gray-500 text-sm text-center max-w-md">
          Complete more {examData.examProvider} {examData.level} exams to see detailed analytics and track your progress over time.
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-4 md:p-8 animate-fade-in">
      {/* Header with Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Performance Analytics
          </h2>
          <p className="text-sm text-gray-600">
            {examData.examProvider} {examData.level} •
            Showing {data.historyCount} of {data.requestedRange} requested exams
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Range Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Show last:
            </label>
            <select
              value={examRange}
              onChange={(e) => handleRangeChange(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
            >
              <option value={5}>5 exams</option>
              <option value={10}>10 exams</option>
              <option value={15}>15 exams</option>
              <option value={20}>20 exams</option>
              <option value={30}>30 exams</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh data"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            title="Export analytics data"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* 1. Key Metrics Cards */}
      <ProgressMetricsCards
        overallMetrics={data.overall}
        timeAnalysis={data.time}
        currentExam={currentExam}
        comparison={data.comparison}
      />

      {/* 2. Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overall Trend Line */}
        <OverallPerformanceChart
          data={data.overall.chartData}
          averageScore={data.overall.averageScore}
        />

        {/* Section Radar Comparison */}
        <SectionRadarChart data={data.radarData} />
      </div>

      {/* 3. Strengths & Weaknesses */}
      <StrengthsWeaknesses
        strengths={data.strengths}
        weaknesses={data.weaknesses}
      />

      {/* 4. Detailed Section Trends */}
      <SectionTrendCharts sectionMetrics={data.sections} />

      {/* Info Footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
        <p className="text-sm text-blue-800">
          💡 <strong>Tip:</strong> Take more exams regularly to see more accurate trends and identify patterns in your performance.
        </p>
      </div>
    </div>
  );
};

export default ExamAnalytics;